import { Col, Card, Button, Row } from 'react-bootstrap'
import InviteUserModal from '@/components/InviteUserModal'
import { useState, memo } from 'react'
import './ProjectCard.scss'
import { format } from 'date-fns'
import RecordActionButtons from '@/components/RecordActionButtons'
import { RecordType } from '@/components/shared/DeleteRecordModal'
import { useToaster } from '@/components/shared/ToasterProvider'
import { UserProjectRoleRole } from '@/types/Enums'
import { userCurrentUser } from '@/features/apiSliceWithCurrentUser'
import { useToggleProjectBlockedMutation, useUpdateUPRRoleMutation, useToggleUPRBlockedMutation } from '@/features/apiSliceWithProjects'
import { Project } from '@/types'
import classNames from 'classnames'
import useToastMutation from '@/hooks/useToastMutation'

interface ProjectProps {
  project: Project,
}

export default memo(function ProjectCard({
  project,
}: ProjectProps) {
  const currentUser = userCurrentUser()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const showToaster = useToaster()
  const [toggleProjectBlocked, { isLoading: isToggleProjectBlockedLoading }] = useToastMutation(useToggleProjectBlockedMutation)
  const [updateUPRRole, { isLoading: isUpdateUPRRoleLoading }] = useToastMutation(useUpdateUPRRoleMutation)
  const [toggleUPRBlocked, { isLoading: isToggleUPRBlocked }] = useToastMutation(useToggleUPRBlockedMutation)

  const { id: projectId, title, summary, createdAt, viewCount, userProjectRoles } = project

  const isAllBlocked = userProjectRoles?.every(upr => upr.blocked) ?? false
  const isProjectEditor = userProjectRoles?.some(upr => upr.userId === currentUser?.id && upr.role === 'editor')
  const canManageProjectUsers = currentUser?.role === 'admin' || isProjectEditor

  const handleToggleProjectBlocked = async () => {
    const blocked = !isAllBlocked
    await toggleProjectBlocked({ projectId, blocked })
    showToaster({ message: `All users have been ${blocked ? 'blocked' : 'unblocked'} successfully!` })
  }

  const handleUpdateUPRole = async (userProjectRoleId: number, role: UserProjectRoleRole) => {
    await updateUPRRole({ userProjectRoleId, role })
    showToaster({ message: `User role has been updated to ${role} successfully!` })
  }

  const handleToggleUPRBlocked = async (projectUserRoleId: number) => {
    const { data: upr } = await toggleUPRBlocked(projectUserRoleId)
    showToaster({ message: `User is ${upr?.blocked ? 'blocked' : 'unblocked'}` })
  }

  const isLoading = isToggleProjectBlockedLoading || isUpdateUPRRoleLoading || isToggleUPRBlocked

  // @ts-ignore
  return (
    <>
      <Row key={projectId} className="mb-3 justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <Card className={classNames('shadow-sm ProjectCard', { 'ProjectCard--Loading': isLoading })}>
            <Card.Body>
              <Card.Title className="ProjectCard__Title">{title}</Card.Title>
              <hr className="border-secondary border-1" />
              {summary && <Card.Text className="ProjectCard__Summary">{summary}</Card.Text>}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-muted">
                  Created: {
                  createdAt && !isNaN(new Date(createdAt).getTime())
                  ? format(new Date(createdAt), 'dd LLL yyyy')
                  : 'Unknown'
                }
                </small>
                <small className="text-muted d-flex align-items-center gap-1">
                  <i className="fa-solid fa-eye" />{viewCount}
                </small>
              </div>
              <RecordActionButtons
                idOrSlug={projectId}
                recordType={RecordType.Project}
              >
                <Button
                  variant="outline-primary"
                  size={'sm'}
                  onClick={() => setShowInviteModal(true)}
                >
                  Invite
                </Button>
                {canManageProjectUsers && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleToggleProjectBlocked}
                  >
                    {isAllBlocked ? 'Unblock all users' : 'Block all users'}
                  </Button>
                )}
              </RecordActionButtons>
              {canManageProjectUsers && <hr className="my-4 border-secondary border-1" />}
              {canManageProjectUsers &&
                <div className="d-flex flex-column gap-2">
                  {userProjectRoles?.map(upr => (
                    <div key={upr.id} className="d-flex justify-content-between">
                      <div className="d-flex gap-2 align-items-center">
                        <small className="text-muted">{upr.user?.email}</small>
                        <small className="d-flex text-muted gap-1 align-items-center">
                          <i className="fa-solid fa-eye" />{upr.viewCount}
                        </small>
                      </div>
                      <div className="d-flex gap-2 ProjectCard__UserActions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleToggleUPRBlocked(upr.id)}
                        >
                          {upr.blocked ? 'Unblock access' : 'Block access'}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleUpdateUPRole(upr.id, upr.role === 'viewer' ? 'editor' : 'viewer')}
                        >
                          {`Make ${upr.role === 'viewer' ? 'editor' : 'viewer'}`}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {showInviteModal && (
        <InviteUserModal
          projectId={projectId}
          visible
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </>
  )
})
